import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import './AccessReviews.css';
import API_URL from '../config';

const AccessReviews = () => {
  const { token, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ campaign_name: '', resource_type: 'GROUP' });

  const fetchReviews = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setReviews(data);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const fetchReviewItems = useCallback(async (reviewId) => {
    setItemsLoading(true);
    try {
      const res = await fetch(`${API_URL}/reviews/${reviewId}/items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setReviewItems(data);
    } catch (err) {
      console.error('Failed to fetch review items:', err);
    } finally {
      setItemsLoading(false);
    }
  }, [token]);

  const handleSelectReview = (review) => {
    setSelectedReview(review);
    setMessage('');
    setError('');
    fetchReviewItems(review.id);
  };

  const handleCreateReview = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_URL}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      setShowModal(false);
      setForm({ campaign_name: '', resource_type: 'GROUP' });
      fetchReviews();
    } catch (err) {
      setError('Failed to create review campaign');
    }
  };

  const handleDecision = async (itemId, decision) => {
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_URL}/reviews/${selectedReview.id}/items/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ decision })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      fetchReviewItems(selectedReview.id);
    } catch (err) {
      setError('Failed to record decision');
    }
  };

  const handleComplete = async () => {
    try {
      const res = await fetch(`${API_URL}/reviews/${selectedReview.id}/complete`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      fetchReviews();
      setSelectedReview({ ...selectedReview, status: 'COMPLETED' });
    } catch (err) {
      setError('Failed to complete review');
    }
  };

  const pendingCount = reviewItems.filter(i => i.decision === 'PENDING').length;
  const totalCount = reviewItems.length;
  const progressPct = totalCount > 0 ? Math.round(((totalCount - pendingCount) / totalCount) * 100) : 0;

  const isAdmin = user?.role === 'ADMIN';

  return (
    <Layout>
      <div className="reviews-header">
        <h1>Access Reviews</h1>
        {isAdmin && (
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            + New Campaign
          </button>
        )}
      </div>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <p style={{ fontSize: '14px', color: '#999' }}>Loading campaigns...</p>
      ) : reviews.length === 0 ? (
        <div className="empty-state">No access review campaigns yet. Create one to get started.</div>
      ) : (
        <div className="campaigns-grid">
          {reviews.map(review => (
            <div
              key={review.id}
              className={`campaign-card ${review.status} ${selectedReview?.id === review.id ? 'selected' : ''}`}
              onClick={() => handleSelectReview(review)}
            >
              <div className="campaign-name">{review.campaign_name}</div>
              <div className="campaign-meta">
                Created by {review.created_by_name} — {new Date(review.created_at).toLocaleDateString()}
              </div>
              <span className={`campaign-status status-${review.status}`}>{review.status}</span>
            </div>
          ))}
        </div>
      )}

      {selectedReview && (
        <div className="review-detail">
          <div className="review-detail-header">
            <h2>{selectedReview.campaign_name}</h2>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#666' }}>
                {progressPct}% complete ({totalCount - pendingCount}/{totalCount})
              </span>
              {isAdmin && selectedReview.status === 'ACTIVE' && pendingCount === 0 && (
                <button className="btn-complete" onClick={handleComplete}>
                  Complete Campaign
                </button>
              )}
            </div>
          </div>

          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${progressPct}%` }} />
          </div>

          <div style={{ marginTop: '24px' }}>
            {itemsLoading ? (
              <p style={{ fontSize: '14px', color: '#999' }}>Loading review items...</p>
            ) : reviewItems.length === 0 ? (
              <div className="empty-state">No items in this review.</div>
            ) : (
              <table className="review-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Resource</th>
                    <th>Type</th>
                    <th>Decision</th>
                    <th>Decided By</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewItems.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>{item.user_name}</td>
                      <td>{item.user_email}</td>
                      <td>{item.resource_name}</td>
                      <td>{item.resource_type}</td>
                      <td>
                        <span className={`decision-badge decision-${item.decision}`}>
                          {item.decision}
                        </span>
                      </td>
                      <td>{item.decided_by_name || '—'}</td>
                      <td>
                        {item.decision === 'PENDING' && selectedReview.status === 'ACTIVE' ? (
                          <div className="decision-actions">
                            <button
                              className="btn-approve"
                              onClick={() => handleDecision(item.id, 'APPROVED')}
                            >
                              Approve
                            </button>
                            <button
                              className="btn-revoke"
                              onClick={() => handleDecision(item.id, 'REVOKED')}
                            >
                              Revoke
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#999' }}>
                            {item.decided_at ? new Date(item.decided_at).toLocaleDateString() : '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>New Access Review Campaign</h2>
            <form onSubmit={handleCreateReview}>
              <div className="form-group">
                <label>Campaign Name</label>
                <input
                  type="text"
                  value={form.campaign_name}
                  onChange={e => setForm({ ...form, campaign_name: e.target.value })}
                  placeholder="e.g. Q4 2026 Group Access Review"
                  required
                />
              </div>
              <div className="form-group">
                <label>Resource Type</label>
                <select
                  value={form.resource_type}
                  onChange={e => setForm({ ...form, resource_type: e.target.value })}
                >
                  <option value="GROUP">Group Memberships</option>
                </select>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AccessReviews;